import { useEffect, useRef, useState } from 'react'
import { Box, Button, FormControl, Input, Text } from '@chakra-ui/react'
import { MockupFieldLabel } from '@/components/forms/parentTicket/FormPanel'
import { useAccessCheck } from '@/hooks/useAccess'
import { getApiErrorMessage } from '@/lib/api/errorMessages'
import { issueClassificationService as service, type ClassificationOptions, type ClassificationOption } from '@/lib/services/issueClassificationService'

type Selection = { issueCategoryId?: number; issueSubCategoryId?: number; issueItemId?: number }
type Props = Selection & { onChange: (values: Selection) => void; inputBaseProps?: Record<string, unknown> }

function ClassificationSelect({ label, level, parentId, value, canCreate, onChange, inputBaseProps }: {
  label: string; level: number; parentId?: number; value?: number; canCreate: boolean
  onChange: (id?: number) => void; inputBaseProps?: Record<string, unknown>
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [result, setResult] = useState<ClassificationOptions | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [retry, setRetry] = useState(0)
  const alive = useRef(true)
  const request = useRef(0)
  const savingRef = useRef(false)
  const disabled = level > 0 && !parentId

  useEffect(() => { alive.current = true; return () => { alive.current = false; request.current++ } }, [])
  useEffect(() => {
    const version = ++request.current
    if (disabled) { setResult(null); return }
    setLoading(true)
    setError('')
    const timer = setTimeout(() => {
      void service.options(level, parentId, query, value).then((data) => {
        if (version === request.current) setResult(data)
      }).catch((err) => {
        if (version === request.current) { setResult(null); setError(getApiErrorMessage(err, 'Could not load options. Please retry.')) }
      }).finally(() => { if (version === request.current) setLoading(false) })
    }, query ? 300 : 0)
    return () => { clearTimeout(timer); request.current++ }
  }, [level, parentId, query, value, retry, disabled])

  const select = (option: ClassificationOption) => {
    onChange(option.id)
    setResult((prev) => prev ? { ...prev, selected: option } : prev)
    setQuery('')
    setOpen(false)
  }
  const add = async () => {
    if (savingRef.current || !result?.canCreate || !canCreate || loading) return
    savingRef.current = true
    setSaving(true)
    setError('')
    try {
      const option = await service.add(query.trim(), level, parentId)
      if (!alive.current) return
      select(option)
      setNotice('Catalog entry saved. Save the ticket to keep this selection.')
    } catch (err) {
      if (alive.current) setError(getApiErrorMessage(err, 'Could not add entry. Please retry.'))
    } finally {
      savingRef.current = false
      if (alive.current) setSaving(false)
    }
  }

  return <FormControl mb="10px">
    <MockupFieldLabel>{label}</MockupFieldLabel>
    <Box position="relative" onBlur={(event) => {
      if (savingRef.current) return
      if (!event.currentTarget.contains(event.relatedTarget as Node)) { setOpen(false); setQuery('') }
    }} onKeyDown={(event) => { if (event.key === 'Escape') { setOpen(false); setQuery('') } }}>
      <Input {...inputBaseProps} size="sm" aria-label={label} autoComplete="off"
        value={open ? query : result?.selected?.name ?? (value ? `Saved selection #${value}` : '')}
        placeholder={disabled ? 'Select parent first' : `Search ${label.toLowerCase()}`}
        isDisabled={disabled || saving} maxLength={255}
        onFocus={() => setOpen(true)}
        onChange={(event) => { request.current++; setLoading(true); setQuery(event.target.value); setOpen(true); setNotice('') }}
        onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault() }} />
      {value ? <Button type="button" size="xs" variant="link" mt={1} isDisabled={saving}
        onClick={() => { onChange(undefined); setQuery(''); setOpen(false); setNotice('') }}>Clear {label.toLowerCase()}</Button> : null}
      {result?.selected?.unavailable ? <Text fontSize="xs">Saved entry is inactive or archived. It can be retained unchanged.</Text> : null}
      {open && !disabled ? <Box position="absolute" zIndex={20} bg="white" borderWidth="1px" borderRadius="md" shadow="md" width="100%" maxH="260px" overflowY="auto" p={2}>
        {loading ? <Text fontSize="sm" role="status">Loading…</Text> : <>
          {result?.matches.map((option) => <Button type="button" key={option.id} variant="ghost" size="sm" width="100%" justifyContent="flex-start" isDisabled={saving} onClick={() => select(option)}>{option.name}</Button>)}
          {!!result?.suggestions.length && <Text fontSize="xs">Did you mean?</Text>}
          {result?.suggestions.map((option) => <Button type="button" key={option.id} variant="ghost" size="sm" width="100%" justifyContent="flex-start" isDisabled={saving} onClick={() => select(option)}>{option.name}</Button>)}
          {!error && result && !result.matches.length && !result.suggestions.length && <Text fontSize="sm">No matching options under this parent.</Text>}
          {!error && canCreate && result?.canCreate && <Button type="button" size="sm" whiteSpace="normal" height="auto" py={2} isLoading={saving} onClick={() => void add()}>Add {label.toLowerCase()} “{query.trim()}”{level > 0 ? ` under ${result.parentPath || 'selected parent'}` : ''}</Button>}
        </>}
        {error && <><Text role="alert" fontSize="sm" color="red.600">{error}</Text><Button type="button" size="xs" onClick={() => setRetry((n) => n + 1)}>Retry</Button></>}
      </Box> : null}
    </Box>
    {notice && <Text role="status" fontSize="xs">{notice}</Text>}
  </FormControl>
}

export function ParentTicketCategoryCascade({ issueCategoryId, issueSubCategoryId, issueItemId, onChange, inputBaseProps }: Props) {
  const access = useAccessCheck('/service-catalog/issue-categories')
  return <>
    <ClassificationSelect label="Issue category" level={0} value={issueCategoryId} canCreate={access.can_create} inputBaseProps={inputBaseProps}
      onChange={(id) => { if (id !== issueCategoryId) onChange({ issueCategoryId: id, issueSubCategoryId: undefined, issueItemId: undefined }) }} />
    <ClassificationSelect key={`sub-${issueCategoryId}`} label="Sub-category" level={1} parentId={issueCategoryId} value={issueSubCategoryId} canCreate={access.can_create} inputBaseProps={inputBaseProps}
      onChange={(id) => { if (id !== issueSubCategoryId) onChange({ issueCategoryId, issueSubCategoryId: id, issueItemId: undefined }) }} />
    <ClassificationSelect key={`item-${issueCategoryId}-${issueSubCategoryId}`} label="Item" level={2} parentId={issueSubCategoryId} value={issueItemId} canCreate={access.can_create} inputBaseProps={inputBaseProps}
      onChange={(id) => onChange({ issueCategoryId, issueSubCategoryId, issueItemId: id })} />
  </>
}
